import useField from '@hooks/use-field-hook';
import {
  beforeDateTime,
  lessThan,
  onlyNumbers,
  required,
} from '@util/validators';
import { useBinaryOptions } from '@util/constant-options';
import useFieldsWatcher from '@hooks/use-fields-watcher';
import { useGetCasePriorityOptionsQuery } from '@api/endpoints/case-priority.api';
import { useGetCaseSeverityOptionsQuery } from '@api/endpoints/case-severity.api';
import { useGetFrequencyTypeOptionsQuery } from '@api/endpoints/frequency-type.api';
import { useGetCompanyLocationDetailOptionsQuery } from '@api/endpoints/company-location.api';
import { useEffect, useMemo, useState } from 'react';
import { useGetCaseTypeOptionsQuery } from '@api/endpoints/case-type.api';
import {
  useCreateIncidentLocationMutation,
  useGetCaseDetailQuery,
  useUpsertCaseOverviewMutation,
} from '@api/endpoints/case.api';
import { UpsertCaseOverviewRequest } from '@api/types/case/upsert-case-overview.request';
import { ApiError } from '@api/types/api-error';
import { usePageAlertVariants } from '@components/alerts';
import { useAppDispatch, useAppSelector } from '@store/store';
import { selectCreateAnalysisType } from '@store/create-analysis-process/selectors';
import { setCurrentCase } from '@store/create-analysis-process/create-analysis-slice';
import { CompanyLocationResource } from '@api/types/company/company-location/company-location.resource';
import { useIsPath } from '@util/path-util';
import useSystemText from '@hooks/use-system-text';
import { useGetCaseAnalysisTypesQuery } from '@api/endpoints/case-analysis-type.api';
import { manuallySetUserCaseRole } from '@store/rca-editor/rca-editor-slice';
import { isNullOrEmpty } from '@util/string-util';
import FunctionalLocationForm, {
  FunctionalLocationFormProps,
} from '@pages/app/company-profile/forms/functional-location-form';
import { useUiPopup } from '@components/ui-popup/ui-popup-provider';
import { RCA_BOX_TEXT_MAX_LENGTH } from '@pages/app/rca/tabs/components/node-types/rca-default-node';

export default function useOverviewStep(caseId?: number) {
  const isEdit = useIsPath('/rca/edit', { startsWith: true });
  const { showPopup } = useUiPopup();
  const { showErrorMessage, showSuccessMessage } = usePageAlertVariants();
  const dispatch = useAppDispatch();
  const { systemText } = useSystemText();
  const [startDateTimePickerClosed, setStartDateTimePickerClosed] =
    useState<number>();

  const { data: caseDetails } = useGetCaseDetailQuery(caseId ?? -1, {
    skip: caseId == null,
  });
  const { data: caseTypeOptions, isLoading: loadingCaseTypeOptions } =
    useGetCaseTypeOptionsQuery();
  const { data: priorityOptions, isLoading: loadingPriorityOptions } =
    useGetCasePriorityOptionsQuery();
  const { data: severityOptions, isLoading: loadingSeverityOptions } =
    useGetCaseSeverityOptionsQuery();
  const { data: frequencyTypeOptions, isLoading: loadingFrequencyTypeOptions } =
    useGetFrequencyTypeOptionsQuery();
  const {
    data: companyLocationOptions,
    isLoading: loadingCompanyLocationOptions,
  } = useGetCompanyLocationDetailOptionsQuery();

  const [incidentLocations, setIncidentLocations] = useState<
    Array<CompanyLocationResource>
  >([]);

  const { data } = useGetCaseDetailQuery(caseId ?? -1, {
    skip: caseId == null,
  });

  const [upsert, { isLoading: isSubmitting }] = useUpsertCaseOverviewMutation();

  const caseAnalysisType =
    useAppSelector(selectCreateAnalysisType) ?? data?.caseAnalysisTypeId;
  const { data: caseAnalysisTypes } = useGetCaseAnalysisTypesQuery({});
  const investigationName = useField<string>([required()], data?.name);
  const description = useField<string>([required()], data?.description);
  const focalPoint = useField<string>(
    [required(), lessThan(RCA_BOX_TEXT_MAX_LENGTH)],
    data?.focalPoint
  );
  const caseType = useField<Array<number>>(
    [required()],
    useMemo(() => data?.caseTypes.map(({ id }) => id) ?? [], [data?.caseTypes])
  );
  const priority = useField<number>([required()], data?.casePriorityId);
  const severity = useField<number>([required()], data?.caseSeverityId);
  const endDate = useField<string>([], data?.endDate);
  const startDate = useField<string>(
    [required(), beforeDateTime(endDate.value, {}, 'Must be before end date')],
    data?.startDate
  );
  const frequency = useField<string>(
    [onlyNumbers(), required()],
    data?.frequencyValue.toString()
  );
  const duration = useField<number>([required()], data?.frequencyTypeId);
  const frequencySummary = useField<string>([], data?.frequencyNotes);
  const reportingLocation = useField<CompanyLocationResource>(
    [required()],
    useMemo(() => {
      return data?.reportingLocation as CompanyLocationResource;
    }, [data?.reportingLocation])
  );
  const incidentAtReportingLocation = useField<boolean>(
    [required()],
    useMemo(() => {
      if (data == null) {
        return undefined;
      }

      return (
        data.reportingLocation.companyLocationId ===
        data.incidentLocation?.companyLocationId
      );
    }, [data])
  );
  const incidentLocation = useField<CompanyLocationResource>(
    [
      required({
        when: () =>
          incidentAtReportingLocation.value != null &&
          !incidentAtReportingLocation.value,
      }),
    ],
    useMemo(() => {
      return data?.incidentLocation as CompanyLocationResource;
    }, [data?.incidentLocation])
  );

  const { isValid, isDirty, validateAll, scrollToTopMostError } =
    useFieldsWatcher([
      investigationName,
      description,
      focalPoint,
      caseType,
      priority,
      severity,
      startDate,
      endDate,
      frequency,
      duration,
      frequencySummary,
      reportingLocation,
      incidentAtReportingLocation,
      incidentLocation,
    ]);

  const isLoading =
    loadingPriorityOptions ||
    loadingSeverityOptions ||
    loadingFrequencyTypeOptions ||
    loadingCompanyLocationOptions ||
    loadingCaseTypeOptions;

  const canSubmit =
    !isSubmitting && !isLoading && isValid && (!isEdit || isDirty);

  const binaryOptions = useBinaryOptions();

  const onSubmit = () => {
    if (!validateAll()) {
      scrollToTopMostError();
      return false;
    }

    return upsert({
      caseId,
      caseAnalysisTypeId: caseAnalysisType! as number,
      name: investigationName.value,
      description: description.value,
      focalPoint: focalPoint.value,
      caseTypes: caseType.value,
      casePriorityId: priority.value,
      caseSeverityId: severity.value,
      startDate: startDate.value,
      endDate: endDate.value,
      frequencyValue: +frequency.value,
      frequencyTypeId: duration.value,
      frequencyNotes: frequencySummary.value,
      reportingLocationId: reportingLocation.value.companyLocationId,
      sameLocationForIncident: incidentAtReportingLocation.value,
      incidentLocationId: incidentLocation.value?.companyLocationId,
    })
      .unwrap()
      .then((resource) => {
        if (isEdit) {
          showSuccessMessage('Updated overview');

          // Hard reload so chain is fresh
          window.location.reload();
        } else {
          // This user created the case, set their case role to owner so permissions work down the line
          dispatch(manuallySetUserCaseRole('Owner'));
        }
        dispatch(setCurrentCase(resource));

        return true;
      })
      .catch(({ message, errors }: ApiError<UpsertCaseOverviewRequest>) => {
        showErrorMessage(errors?.caseId ?? errors?.chainItemId ?? message);

        investigationName.setError(errors?.name);
        description.setError(errors?.description);
        focalPoint.setError(errors?.focalPoint);
        caseType.setError(errors?.caseTypes);
        priority.setError(errors?.casePriorityId);
        severity.setError(errors?.caseSeverityId);
        startDate.setError(errors?.startDate);
        endDate.setError(errors?.endDate);
        frequency.setError(errors?.frequencyValue);
        duration.setError(errors?.frequencyTypeId);
        frequencySummary.setError(errors?.frequencyNotes);
        reportingLocation.setError(errors?.reportingLocationId);
        incidentAtReportingLocation.setError(errors?.sameLocationForIncident);
        incidentLocation.setError(errors?.incidentLocationId);

        scrollToTopMostError();

        return false;
      });
  };

  const endDateValue = endDate.value;
  const startDateValue = startDate.value;
  const validateStartDate = startDate.validate;
  useEffect(() => {
    if (!isNullOrEmpty(startDateValue) && !isNullOrEmpty(endDateValue)) {
      validateStartDate(true);
    }
  }, [endDateValue, validateStartDate, startDateValue]);

  const filteredCompanyLocationOptions = useMemo(() => {
    return (
      companyLocationOptions?.filter(
        (x) =>
          !x.disabled ||
          reportingLocation.value?.companyLocationId === x.companyLocationId ||
          incidentLocation.value?.companyLocationId === x.companyLocationId
      ) ?? []
    );
  }, [
    companyLocationOptions,
    incidentLocation.value?.companyLocationId,
    reportingLocation.value?.companyLocationId,
  ]);

  useEffect(() => {
    const incidentLocation = caseDetails?.incidentLocation;
    if (incidentLocation == null || companyLocationOptions == null) {
      return;
    }

    const isCompanyLocation = companyLocationOptions.some(
      (x) => x.companyLocationId === incidentLocation.companyLocationId
    );
    if (isCompanyLocation) {
      return;
    }

    setIncidentLocations((prev) => {
      const alreadyExists = prev.some(
        (x) => x.companyLocationId === incidentLocation.companyLocationId
      );
      if (alreadyExists) {
        return prev;
      }
      return [...prev, { ...incidentLocation, disabled: false }];
    });
  }, [caseDetails?.incidentLocation, companyLocationOptions]);

  const addNewIncidentLocation = async () => {
    const newIncidentLocation = await showPopup<
      FunctionalLocationFormProps,
      CompanyLocationResource
    >(FunctionalLocationForm, {
      allowPinOnMap: true,
      creationMutation: useCreateIncidentLocationMutation,
      isIncidentLocation: true,
    });

    if (newIncidentLocation != null) {
      setIncidentLocations((prev) => [...prev, newIncidentLocation]);
      incidentLocation.set(newIncidentLocation);
    }
  };

  return {
    investigationName,
    description,
    focalPoint,
    rcaType: caseType,
    priority,
    severity,
    startDate,
    endDate,
    frequency,
    duration,
    frequencySummary,
    reportingLocation,
    incidentAtReportingLocation,
    incidentLocation,
    binaryOptions,
    caseTypeOptions,
    frequencyTypeOptions,
    companyLocationOptions: filteredCompanyLocationOptions,
    incidentLocationOptions: [
      ...incidentLocations,
      ...filteredCompanyLocationOptions,
    ],
    priorityOptions,
    severityOptions,
    isValid,
    isLoading,
    isSubmitting,
    isEdit,
    canSubmit,
    systemText,
    addNewIncidentLocation,
    onSubmit,
    caseAnalysisTypeName:
      caseAnalysisTypes?.find((x) => x.caseAnalysisTypeId === caseAnalysisType)
        ?.name ?? '',
    endDateWatchValue: startDateTimePickerClosed,
    onStartDateTimeClosed: () => {
      if (startDate.value) {
        setStartDateTimePickerClosed(new Date().getTime());
      }
    },
  };
}

export type OverviewStepState = ReturnType<typeof useOverviewStep>;
